import { useEffect, useState, useMemo } from "react";
import useDigitalTheme from "../hooks/useDigitalTheme";
import DataTable from "react-data-table-component";
import { useSelector } from "react-redux";
import { FaFileAlt, FaSearch } from "react-icons/fa";
import BarraMenu from "../../components/BarraMenu/";
import PageHero from "../../components/PageHero";
import { httpApiPostText } from "../../lib";
import { useNavigate } from "react-router";
import API_HOST, { HEADERS_JSON } from "../../ApiConfig";
import horizonLogo  from "../../assets/logos/Horizon.png";
import nettoLogo    from "../../assets/logos/NETTO.png";
import digitalLogo  from "../../assets/logos/digitalProf-it.png";
import impulsapLogo from "../../assets/logos/impulsap.png";
const MIN_CHARS   = 3;
const DEBOUNCE_MS = 400;
const REPORTE_STATE_KEY = "reporteEvaluacionesState";
const loadReporteState = () => {
  try {
    const saved = localStorage.getItem(REPORTE_STATE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

const GROUP_LOGOS_RPT: Record<string, string> = {
  horizon:  horizonLogo,
  netto:    nettoLogo,
  digital:  digitalLogo,
  impulsap: impulsapLogo,
  impulsab: impulsapLogo,
};

const getClientLogo = (namecompany: string | undefined): string | null => {
  if (!namecompany) return null;
  const key = namecompany.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z]/g, '');
  if (key.includes('digital')) return null;
  const match = Object.keys(GROUP_LOGOS_RPT).find(k => key.includes(k));
  return match ? GROUP_LOGOS_RPT[match] : null;
};

interface Colaborador {
  id_user: string;
  name: string;
}

interface EvaluacionRow {
  id_evaluation: number;
  id_user: string;
  name: string;
  description: string;
  user_evaluation_date: string;
  namecompany: string;
  key: string;
  Acciones: React.ReactNode;
}

// ── Columnas tabla evaluaciones ───────────────────────────────────────────────
const buildReporteColumns = (navigate: any) => [
  { name: "Id", selector: (row: any) => row.id_user, sortable: true, width: "90px", omit: true },
  { name: "Nombre", selector: (row: any) => row.name, sortable: true, width: "250px",
    cell: (row: any) => <div style={{ fontWeight: "bold", color: "#2c3e50" }}>{row.name}</div> },
  { name: "Cliente", selector: (row: any) => row.description, sortable: true, width: "250px",
    cell: (row: any) => <div style={{ color: "#34495e" }}>{row.description || "Sin asignar"}</div> },
  { name: "Grupo de Negocio", selector: (row: any) => row.namecompany, sortable: true, width: "250px",
    cell: (row: any) => <div style={{ color: "#3498db", fontWeight: "500" }}>{row.namecompany}</div> },
  { name: "Fecha", selector: (row: any) => row.user_evaluation_date ? row.user_evaluation_date.substring(0,10) : "",
    sortable: true, width: "150px",
    cell: (row: any) => (
      <div style={{ backgroundColor: row.user_evaluation_date ? "#e8fcf1" : "#f8f9fa",
        padding: "6px 10px", borderRadius: "4px", fontSize: "13px",
        color: row.user_evaluation_date ? "#0d6832" : "#6c757d", textAlign: "center" as const }}>
        {row.user_evaluation_date ? row.user_evaluation_date.substring(0,10) : "Sin fecha"}
      </div>
    ) },
  { name: "Acciones", selector: (row: any) => row.Acciones, grow: 2, center: true },
];

// ── Carga evaluaciones de un colaborador seleccionado (client-side, volumen bajo) ──
const rptCargarEvaluaciones = async (
  idUser: string, nombre: string,
  emp: any, setData: any, navigate: any
) => {
  if (!idUser) { setData([]); return; }
  // Para el panel Digital (idbusiness_group=1) traemos TODOS los reportes
  // del colaborador sin filtrar por cliente — incluye evaluaciones de fechas
  // anteriores cuando no existia la separacion de 10/30 preguntas
  const isDigital = Number(emp.idbusiness_group) === 1 &&
    (emp.id_client == null || Number(emp.id_client) <= 1);
  const req = {
    valid: "getAllEvaluation",
    id_collaborator: idUser,
    id_client:          isDigital ? null : emp.id_client,
    idbusiness_group:   isDigital ? null : emp.idbusiness_group,
  };
  const response = await httpApiPostText(API_HOST, "POST", HEADERS_JSON, req);
  if (!response.message || typeof response.message !== "string") { setData([]); return; }
  let dta: any;
  try { dta = JSON.parse(response.message); } catch { setData([]); return; }
  if (!dta || !Array.isArray(dta.payload)) { setData([]); return; }
  const newObj = dta.payload
    .map((item: any, idx: number) => ({
      id_evaluation: item.id_evaluation, id_user: item.user_id,
      name: nombre, description: item.description,
      user_evaluation_date: item.date_close, namecompany: item.namecompany,
      key: `${item.id_evaluation || idx}_${item.user_id}`,
      Acciones: (
        <div className="d-flex justify-content-center" key={`${item.id_evaluation || idx}`}>
          <button id={`${item.user_id}`}
            onClick={() => navigate(`/formatoEvaluaciones/${item.user_id}/${item.id_evaluation}`)}
            className="action-button"
            style={{ backgroundColor: "#28a745", width: "38px", height: "38px",
              display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: "50%", border: "none", cursor: "pointer", padding: 0 }}
            title="Ver evaluación">
            <FaFileAlt size={18} color="white" />
          </button>
        </div>
      ),
    }));
  setData([...newObj]);
};

// ── Búsqueda server-side de colaboradores (paginada, debounce) ────────────────
const rptBuscarColaboradores = async (
  emp: any, search: string, page: number
): Promise<{ resultados: Colaborador[]; total: number }> => {
  try {
    const res = await httpApiPostText(API_HOST, "POST", HEADERS_JSON, {
      valid: "getCollaboratorsEvaluatorPaged",
      id_evaluator:       emp.id_user,
      id_client:          emp.id_client || null,
      idbusiness_group:   emp.idbusiness_group || null,
      search:             search.trim(),
      page,
      per_page: 10,
    });
    if (!res?.message) return { resultados: [], total: 0 };
    const raw = res.message as string;
    const dta = JSON.parse(raw.substring(raw.indexOf("{")));
    if (dta.code !== 1 || !Array.isArray(dta.payload)) return { resultados: [], total: 0 };
    
    const resultados = dta.payload.map((i: any) => ({
      id_user: String(i.id_collaborator),
      name:    i.collaborator_name,
    }));
    return { resultados, total: dta.total ?? resultados.length };
  } catch {
    return { resultados: [], total: 0 };
  }
};

const ReporteEvaluacionesPage = () => {
  useDigitalTheme();
  
  // Cargar estado guardado de localStorage
  const savedState = loadReporteState();
  
  const [data, setData]                   = useState<EvaluacionRow[]>(savedState.data || []);
  const [searchInput, setSearchInput]     = useState(savedState.searchInput || "");
  const [searchActive, setSearchActive]   = useState(savedState.searchActive || "");
  const [sugerencias, setSugerencias]     = useState<Colaborador[]>([]);
  const [showDrop, setShowDrop]           = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const emp: any = useSelector((state: any) => state.emp);
  const navigate  = useNavigate();

  const isDigitalPanel = useMemo(() => Number(emp?.idbusiness_group) === 1, [emp?.idbusiness_group]);

  const [filtroCliente,   setFiltroCliente]   = useState(savedState.filtroCliente || "");
  const [filtroGrupo,     setFiltroGrupo]     = useState(savedState.filtroGrupo || "");
  const [filtroFechaDesde, setFiltroFechaDesde] = useState(savedState.filtroFechaDesde || "");
  const [filtroFechaHasta, setFiltroFechaHasta] = useState(savedState.filtroFechaHasta || "");

  // Reiniciar al cambiar panel - PERO conservando filtros si hay estado guardado
useEffect(() => {
  if (emp.first_name !== "") {
    // Solo limpiar si NO hay datos guardados en localStorage
    const savedState = loadReporteState();
    const hasSavedData = savedState.data && savedState.data.length > 0;
    
    if (!hasSavedData) {
      setData([]);
      setSearchInput("");
      setSearchActive("");
      setSugerencias([]);
      setShowDrop(false);
      setFiltroCliente("");
      setFiltroGrupo("");
      setFiltroFechaDesde("");
      setFiltroFechaHasta("");
    } else {
      // Restaurar desde localStorage
      setData(savedState.data || []);
      setSearchInput(savedState.searchInput || "");
      setSearchActive(savedState.searchActive || "");
      setFiltroCliente(savedState.filtroCliente || "");
      setFiltroGrupo(savedState.filtroGrupo || "");
      setFiltroFechaDesde(savedState.filtroFechaDesde || "");
      setFiltroFechaHasta(savedState.filtroFechaHasta || "");
    }
  }
}, [emp.id_client, emp.idbusiness_group]);

  const columns  = buildReporteColumns(navigate);
  const cargarEvaluaciones = (idUser: string, nombre: string) =>
    rptCargarEvaluaciones(idUser, nombre, emp, setData, navigate);

  // ── Debounce: busca en servidor con 3+ chars, 400ms de pausa ─────────────
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === searchActive) return;
    if (trimmed.length > 0 && trimmed.length < MIN_CHARS) {
      setSugerencias([]);
      setShowDrop(false);
      return;
    }
    if (trimmed.length === 0) {
      setSugerencias([]);
      setShowDrop(false);
      setSearchActive("");
      return;
    }
    const timer = setTimeout(async () => {
      setSearchActive(trimmed);
      setLoadingSearch(true);
      const { resultados } = await rptBuscarColaboradores(emp, trimmed, 1);
      setSugerencias(resultados);
      setShowDrop(resultados.length > 0);
      setLoadingSearch(false);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const pagOptions = {
    rowsPerPageText: "Filas por páginas",
    rangeSeparatorText: "de",
    selectAllRowsItem: true,
    selectAllRowsItemText: "Todos",
  };

  // Aplicar filtros client-side sobre las evaluaciones ya cargadas
  const dataFiltrada = useMemo(() => {
    if (!isDigitalPanel) return data;
    return data.filter((row: any) => {
      if (filtroCliente && !row.description?.toLowerCase().includes(filtroCliente.toLowerCase())) return false;
      if (filtroGrupo   && !row.namecompany?.toLowerCase().includes(filtroGrupo.toLowerCase()))   return false;
      if (filtroFechaDesde && row.user_evaluation_date < filtroFechaDesde) return false;
      if (filtroFechaHasta && row.user_evaluation_date > filtroFechaHasta) return false;
      return true;
    });
  }, [data, isDigitalPanel, filtroCliente, filtroGrupo, filtroFechaDesde, filtroFechaHasta]);

  return (
    <div className="container bg-white page-shell">
      <BarraMenu />
      <div className="page-shell__body">
        <div className="page-breadcrumb">Home / Reporte / Evaluaciones</div>
        <PageHero
          icon={<FaFileAlt />}
          title="Reporte de Evaluaciones"
          subtitle="Consulta y visualización de evaluaciones finalizadas por colaborador"
          badge={
            data.length > 0 ? (
              <div className="dashboard-hero__badge d-flex align-items-center gap-2">
                {getClientLogo(data[0].namecompany) && (
                  <img
                    src={getClientLogo(data[0].namecompany) as string}
                    alt={data[0].namecompany || "Logo"}
                    style={{ height: "32px", maxWidth: "100px", objectFit: "contain" }}
                  />
                )}
                {data.length} evaluaciones
              </div>
            ) : (
              <div className="dashboard-hero__badge">Reportes</div>
            )
          }
        />

        <div className="row">
          <div className="col mt-3">
            <div className="m-2 col-lg-6 col-md-12 position-relative">
              <label htmlFor="searchInput" className="m-1">
                Buscar colaborador por nombre o apellido
              </label>

              {/* Input con ícono de búsqueda */}
              <div className="input-group m-1">
                <span className="input-group-text bg-white">
                  {loadingSearch
                    ? <span className="spinner-border spinner-border-sm text-secondary" />
                    : <FaSearch size={13} color="#888" />
                  }
                </span>
                <input
                  id="searchInput"
                  type="text"
                  className="form-control"
                  placeholder={`Escriba nombre o apellido (mín. ${MIN_CHARS} caracteres)...`}
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onFocus={() => { if (sugerencias.length > 0) setShowDrop(true); }}
                  onBlur={() => setTimeout(() => setShowDrop(false), 150)}
                  autoComplete="off"
                />
                {searchInput && (
                  <button className="btn btn-outline-secondary" type="button"
                    onClick={() => { setSearchInput(""); setSugerencias([]); setShowDrop(false); setData([]);
                      setFiltroCliente(""); setFiltroGrupo(""); setFiltroFechaDesde(""); setFiltroFechaHasta(""); }}>
                    ✕
                  </button>
                )}
              </div>

              {/* Hint mín chars */}
              {searchInput.length > 0 && searchInput.trim().length < MIN_CHARS && (
                <small className="text-muted ms-1" style={{ fontSize: "11px" }}>
                  Escribe al menos {MIN_CHARS} caracteres
                </small>
              )}

              {/* Dropdown de sugerencias */}
              {showDrop && sugerencias.length > 0 && (
                <div
                  className="border rounded shadow-sm bg-white position-absolute"
                  style={{ zIndex: 1000, width: "calc(100% - 8px)", maxHeight: "220px",
                    overflowY: "auto", left: "4px", top: "100%" }}
                >
                  {sugerencias.map((user: any) => (
                    <div
                      key={user.id_user}
                      className="p-2 border-bottom"
                      style={{ cursor: "pointer" }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#f0f4f8")}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = "white")}
                      onMouseDown={() => {
                        setSearchInput(user.name);
                        setSugerencias([]);
                        setShowDrop(false);
                        cargarEvaluaciones(user.id_user, user.name);
                      }}
                    >
                      {user.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Filtros — aparecen solo cuando el colaborador tiene evaluaciones cargadas */}
          {data.length > 0 && isDigitalPanel && (
            <div className="m-2 mt-3 p-3 border rounded bg-light">
              <div className="row g-2">
                <div className="col-md-3 col-sm-6">
                  <label className="form-label small text-muted mb-1">Cliente</label>
                  <input type="text" className="form-control form-control-sm"
                    placeholder="Filtrar por cliente..."
                    value={filtroCliente}
                    onChange={e => setFiltroCliente(e.target.value)} />
                </div>
                <div className="col-md-3 col-sm-6">
                  <label className="form-label small text-muted mb-1">Grupo de Negocio</label>
                  <input type="text" className="form-control form-control-sm"
                    placeholder="Filtrar por grupo..."
                    value={filtroGrupo}
                    onChange={e => setFiltroGrupo(e.target.value)} />
                </div>
                <div className="col-md-3 col-sm-6">
                  <label className="form-label small text-muted mb-1">Fecha desde</label>
                  <input type="date" className="form-control form-control-sm"
                    value={filtroFechaDesde}
                    onChange={e => setFiltroFechaDesde(e.target.value)} />
                </div>
                <div className="col-md-3 col-sm-6">
                  <label className="form-label small text-muted mb-1">Fecha hasta</label>
                  <input type="date" className="form-control form-control-sm"
                    value={filtroFechaHasta}
                    onChange={e => setFiltroFechaHasta(e.target.value)} />
                </div>
              </div>
              {(filtroCliente || filtroGrupo || filtroFechaDesde || filtroFechaHasta) && (
                <div className="mt-2 d-flex align-items-center gap-2">
                  <small className="text-muted">{dataFiltrada.length} resultado{dataFiltrada.length !== 1 ? "s" : ""}</small>
                  <button className="btn btn-sm btn-outline-secondary py-0 px-2" style={{ fontSize: "11px" }}
                    onClick={() => { setFiltroCliente(""); setFiltroGrupo(""); setFiltroFechaDesde(""); setFiltroFechaHasta(""); }}>
                    Limpiar filtros
                  </button>
                </div>
              )}
            </div>
          )}

          <div>
            <p className="mt-4 h5 fw-bold text-dark">Historial Evaluaciones Cerradas</p>
          </div>

          <DataTable
            columns={columns}
            data={dataFiltrada}
            selectableRowsHighlight
            fixedHeader
            pagination
            highlightOnHover
            noDataComponent={
              <div className="p-4 text-center text-muted">
                <p className="mb-0">No hay evaluaciones disponibles</p>
              </div>
            }
            paginationComponentOptions={pagOptions}
            customStyles={{
              table: { style: { backgroundColor: "white", borderRadius: "8px" } },
              headRow: {
                style: {
                  backgroundColor: "var(--theme-table-head-bg, #ffffff)",
                  minHeight: "50px",
                  borderTop: "1px solid var(--theme-table-head-border, #e8ecf0)",
                  borderBottom: "2px solid var(--theme-button-primary, #9bdd33)",
                  fontWeight: "bold",
                  color: "var(--theme-table-head-text, #2c3e50)",
                },
              },
              headCells: { style: { fontSize: "14px", padding: "16px 8px", textTransform: "uppercase" } },
              rows: {
                style: {
                  fontSize: "14px", fontWeight: "500", color: "#333", minHeight: "55px",
                  "&:not(:last-of-type)": { borderBottomStyle: "solid", borderBottomWidth: "1px", borderBottomColor: "#e9ecef" },
                },
                highlightOnHoverStyle: {
                  backgroundColor: "#f8f9ff", borderBottomColor: "#c7d5e3",
                  borderRadius: "4px", transition: "all 0.3s",
                  transform: "translateY(-2px)", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                },
              },
              cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
              pagination: {
                style: {
                  borderTop: "1px solid #dee2e6", backgroundColor: "#f8f9fa",
                  fontSize: "13px", fontWeight: "bold", color: "#555", padding: "16px",
                },
                pageButtonsStyle: {
                  borderRadius: "50%", height: "32px", width: "32px", padding: "5px",
                  margin: "0px 5px", transition: "0.4s", backgroundColor: "transparent",
                  "&:hover:not(:disabled)": { backgroundColor: "#007bff", color: "white" },
                },
              },
            }}
            className="shadow rounded m-3"
          />
        </div>
      </div>
    </div>
  );
};

export default ReporteEvaluacionesPage;